import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { bannerAPI } from '../../services/api';
import { TextAd, CreateTextAdRequest } from '../../types/banner';

interface TextAdTabProps {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const TextAdTab: React.FC<TextAdTabProps> = ({ onError, onSuccess }) => {
  const [textAds, setTextAds] = useState<TextAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTextAd, setEditingTextAd] = useState<TextAd | null>(null);
  const [createForm, setCreateForm] = useState<CreateTextAdRequest>({
    symbolUrl: '',
    headline: '',
    content: '',
    ctaText: '',
    ctaLinkUrl: '',
    startDate: Math.floor(Date.now() / 1000), // 현재 UTC 시간
    endDate: Math.floor((Date.now() + (30 * 24 * 60 * 60 * 1000)) / 1000), // 30일 후 UTC 시간
    isActive: true,
    weight: 1,
    locale: 'all',
  });
  const [editForm, setEditForm] = useState<CreateTextAdRequest>({
    symbolUrl: '',
    headline: '',
    content: '',
    ctaText: '',
    ctaLinkUrl: '',
    startDate: Math.floor(Date.now() / 1000), // 현재 UTC 시간
    endDate: Math.floor((Date.now() + (30 * 24 * 60 * 60 * 1000)) / 1000), // 30일 후 UTC 시간
    isActive: true,
    weight: 1,
    locale: 'all',
  });

  // 파일 업로드 refs
  const symbolImageRef = useRef<HTMLInputElement>(null);
  const editSymbolImageRef = useRef<HTMLInputElement>(null);

  // 파일 상태
  const [symbolImageFile, setSymbolImageFile] = useState<File | null>(null);
  const [editSymbolImageFile, setEditSymbolImageFile] = useState<File | null>(null);

  // 텍스트 광고 목록 로드
  const loadTextAds = async () => {
    try {
      setLoading(true);
      const response = await bannerAPI.getTextAds();

      
      // API 응답 구조: { success: true, data: [...] }
      const rawTextAds = response.data?.data || [];
      
      // 배열이 아닌 경우 빈 배열로 설정
      const textAdsArray = Array.isArray(rawTextAds) ? rawTextAds : [];
      
      setTextAds(textAdsArray);
    } catch (err: any) {
      console.error('Text Ads Error:', err);
      onError(err.response?.data?.error || '텍스트 광고 목록을 불러오는데 실패했습니다.');
      setTextAds([]); // 에러 시 빈 배열로 설정
    } finally {
      setLoading(false);
    }
  };

  // 텍스트 광고 생성
  const handleCreateTextAd = async () => {
    try {
      // FormData 생성
      const formData = new FormData();
      formData.append('headline', createForm.headline);
      formData.append('content', createForm.content);
      formData.append('cta_text', createForm.ctaText);
      formData.append('cta_link_url', createForm.ctaLinkUrl);
      formData.append('start_date', createForm.startDate.toString());
      formData.append('end_date', createForm.endDate.toString());
      formData.append('is_active', createForm.isActive.toString());
      formData.append('weight', createForm.weight.toString());
      formData.append('locale', createForm.locale);

      // 파일 추가
      if (symbolImageFile) {
        formData.append('symbol_image', symbolImageFile);
      }

      await bannerAPI.createTextAd(formData);
      onSuccess('텍스트 광고가 성공적으로 생성되었습니다.');
      setCreateDialogOpen(false);
      setCreateForm({
        symbolUrl: '',
        headline: '',
        content: '',
        ctaText: '',
        ctaLinkUrl: '',
        startDate: Math.floor(Date.now() / 1000),
        endDate: Math.floor((Date.now() + (30 * 24 * 60 * 60 * 1000)) / 1000),
        isActive: true,
        weight: 1,
        locale: 'all',
      });
      // 파일 상태 초기화
      setSymbolImageFile(null);
    } catch (err: any) {
      onError(err.response?.data?.error || '텍스트 광고 생성에 실패했습니다.');
    }
  };

  // 텍스트 광고 수정
  const handleEditTextAd = async (textAdId: number) => {
    const textAd = textAds.find(t => t.textAdId === textAdId);
    if (!textAd) {
      onError('텍스트 광고를 찾을 수 없습니다.');
      return;
    }

    // textAd.startDate와 textAd.endDate는 문자열 날짜 형태 (UTC)
    // API에서 받은 날짜는 UTC 시간이므로, 'Z'를 추가해서 UTC로 명시
    const startDateTimestamp = Math.floor(new Date(textAd.startDate + 'Z').getTime() / 1000);
    const endDateTimestamp = Math.floor(new Date(textAd.endDate + 'Z').getTime() / 1000);
    

    
    setEditForm({
      symbolUrl: textAd.symbolUrl,
      headline: textAd.headline,
      content: textAd.content,
      ctaText: textAd.ctaText,
      ctaLinkUrl: textAd.ctaLinkUrl,
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      isActive: textAd.isActive,
      weight: textAd.weight,
      locale: textAd.locale || 'all',
    });
    setEditingTextAd(textAd);
    setEditDialogOpen(true);
  };

  // 텍스트 광고 수정 저장
  const handleUpdateTextAd = async () => {
    if (!editingTextAd) return;

    try {

      
      // FormData 생성
      const formData = new FormData();
      formData.append('headline', editForm.headline);
      formData.append('content', editForm.content);
      formData.append('cta_text', editForm.ctaText);
      formData.append('cta_link_url', editForm.ctaLinkUrl);
      formData.append('start_date', editForm.startDate.toString());
      formData.append('end_date', editForm.endDate.toString());
      formData.append('is_active', editForm.isActive.toString());
      formData.append('weight', editForm.weight.toString());
      formData.append('locale', editForm.locale);

      // 파일 추가
      if (editSymbolImageFile) {
        formData.append('symbol_image', editSymbolImageFile);
      }

      await bannerAPI.updateTextAd(editingTextAd.textAdId, formData);
      onSuccess('텍스트 광고가 성공적으로 수정되었습니다.');
      setEditDialogOpen(false);
      setEditingTextAd(null);
      setEditForm({
        symbolUrl: '',
        headline: '',
        content: '',
        ctaText: '',
        ctaLinkUrl: '',
        startDate: Math.floor(Date.now() / 1000),
        endDate: Math.floor((Date.now() + (30 * 24 * 60 * 60 * 1000)) / 1000),
        isActive: true,
        weight: 1,
        locale: 'all',
      });
      // 파일 상태 초기화
      setEditSymbolImageFile(null);
      loadTextAds();
    } catch (err: any) {
      onError(err.response?.data?.error || '텍스트 광고 수정에 실패했습니다.');
    }
  };

  // 텍스트 광고 삭제
  const handleDeleteTextAd = async (textAdId: number) => {
    if (!window.confirm('정말로 이 텍스트 광고를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await bannerAPI.deleteTextAd(textAdId);
      onSuccess('텍스트 광고가 성공적으로 삭제되었습니다.');
      loadTextAds();
    } catch (err: any) {
      onError(err.response?.data?.error || '텍스트 광고 삭제에 실패했습니다.');
    }
  };

  useEffect(() => {
    loadTextAds();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Text Ad List
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Add Text Ad
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Card elevation={2}>
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Symbol Image</TableCell>
                    <TableCell>Headline</TableCell>
                    <TableCell>Content</TableCell>
                    <TableCell>CTA Text</TableCell>
                    <TableCell>CTA Link</TableCell>
                    <TableCell>Start Date (KST)</TableCell>
                    <TableCell>End Date (KST)</TableCell>
                    <TableCell>Locale</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {textAds.map((textAd) => (
                    <TableRow key={textAd.textAdId} hover>
                      <TableCell>{textAd.textAdId}</TableCell>
                      <TableCell>
                        {textAd.symbolUrl && (
                          <Avatar
                            src={textAd.symbolUrl}
                            alt="Symbol"
                            sx={{ width: 40, height: 40 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, wordBreak: 'break-all' }}>
                          {textAd.headline}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 150, wordBreak: 'break-all' }}>
                          {textAd.content}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 100, wordBreak: 'break-all' }}>
                          {textAd.ctaText}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: 'break-all' }}>
                          {textAd.ctaLinkUrl ? (
                            <a 
                              href={textAd.ctaLinkUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#1976d2', 
                                textDecoration: 'underline',
                                cursor: 'pointer'
                              }}
                            >
                              {textAd.ctaLinkUrl}
                            </a>
                          ) : (
                            '-'
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {textAd.startDate && textAd.startDate !== '0' 
                            ? (() => {
                                // API에서 받아온 날짜는 UTC 시간이므로 KST로 변환
                                // "2025-08-26 05:48:00" 형태의 문자열
                                const dateString = textAd.startDate;
                                
                                // UTC 시간을 Date 객체로 변환
                                const utcDate = new Date(dateString + 'Z'); // UTC로 명시
                                if (isNaN(utcDate.getTime())) return 'Not set';
                                
                                // UTC → KST 변환 (UTC + 9시간)
                                const kstDate = new Date(utcDate.getTime() + (9 * 60 * 60 * 1000));
                                
                                // KST 시간을 한국어 형식으로 표시
                                const year = kstDate.getUTCFullYear();
                                const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                                const day = String(kstDate.getUTCDate()).padStart(2, '0');
                                const hour = kstDate.getUTCHours();
                                const minute = kstDate.getUTCMinutes();
                                
                                const result = `${year}. ${month}. ${day}. ${hour >= 12 ? '오후' : '오전'} ${String(hour > 12 ? hour - 12 : hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                
                                
                                
                                return result;
                              })()
                            : 'Not set'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {textAd.endDate && textAd.endDate !== '0'
                            ? (() => {
                                // API에서 받아온 날짜는 UTC 시간이므로 KST로 변환
                                // "2025-08-26 05:48:00" 형태의 문자열
                                const dateString = textAd.endDate;
                                
                                // UTC 시간을 Date 객체로 변환
                                const utcDate = new Date(dateString + 'Z'); // UTC로 명시
                                if (isNaN(utcDate.getTime())) return 'Not set';
                                
                                // UTC → KST 변환 (UTC + 9시간)
                                const kstDate = new Date(utcDate.getTime() + (9 * 60 * 60 * 1000));
                                
                                // KST 시간을 한국어 형식으로 표시
                                const year = kstDate.getUTCFullYear();
                                const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                                const day = String(kstDate.getUTCDate()).padStart(2, '0');
                                const hour = kstDate.getUTCHours();
                                const minute = kstDate.getUTCMinutes();
                                
                                const result = `${year}. ${month}. ${day}. ${hour >= 12 ? '오후' : '오전'} ${String(hour > 12 ? hour - 12 : hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                
                                
                                
                                return result;
                              })()
                            : 'Not set'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={textAd.locale || 'all'}
                          size="small"
                          color={textAd.locale === 'all' ? 'default' : 'info'}
                          variant={textAd.locale === 'all' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={textAd.isActive ? '활성' : '비활성'}
                          color={textAd.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {textAd.createdAt 
                            ? new Date(textAd.createdAt).toLocaleDateString('ko-KR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                              })
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditTextAd(textAd.textAdId)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTextAd(textAd.textAdId)}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Text Ad</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  Symbol Image
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => symbolImageRef.current?.click()}
                    sx={{ minWidth: 120 }}
                  >
                    Upload Symbol Image
                  </Button>
                  {symbolImageFile && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="success.main">
                        ✓ {symbolImageFile.name}
                      </Typography>
                      <Box
                        component="img"
                        src={URL.createObjectURL(symbolImageFile)}
                        alt="Symbol Image Preview"
                        sx={{
                          width: '100%',
                          maxWidth: 120,
                          height: 'auto',
                          maxHeight: 120,
                          objectFit: 'contain',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                            borderColor: '#1976d2'
                          }
                        }}
                        onClick={() => window.open(URL.createObjectURL(symbolImageFile), '_blank')}
                        title="Click to view full image"
                      />
                    </Box>
                  )}
                </Box>
                <input
                  id="symbol-image-upload"
                  type="file"
                  ref={symbolImageRef}
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      // 파일 크기 체크 (5MB)
                      if (file.size > 5 * 1024 * 1024) {
                        onError('파일 크기는 5MB 이하여야 합니다.');
                        return;
                      }
                      // 파일 타입 체크
                      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
                      if (!allowedTypes.includes(file.type)) {
                        onError('JPG, JPEG, PNG, GIF 파일만 업로드 가능합니다.');
                        return;
                      }
                      setSymbolImageFile(file);
                    }
                  }}
                  style={{ display: 'none' }}
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Headline"
                value={createForm.headline}
                onChange={(e) => setCreateForm({ ...createForm, headline: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                value={createForm.content}
                onChange={(e) => setCreateForm({ ...createForm, content: e.target.value })}
                margin="normal"
                required
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CTA Text"
                value={createForm.ctaText}
                onChange={(e) => setCreateForm({ ...createForm, ctaText: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CTA Link"
                value={createForm.ctaLinkUrl}
                onChange={(e) => setCreateForm({ ...createForm, ctaLinkUrl: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date (KST)"
                type="datetime-local"
                value={(() => {
                  const date = new Date(createForm.startDate * 1000);
                  const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
                  // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                  const year = kstDate.getUTCFullYear();
                  const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(kstDate.getUTCDate()).padStart(2, '0');
                  const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                  const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                  const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                  

                  
                  return result;
                })()}
                onChange={(e) => {
                  // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                  const inputValue = e.target.value; // "2025-08-25T21:00"
                  
                  // 입력값을 직접 파싱하여 KST 시간 생성
                  const [datePart, timePart] = inputValue.split('T');
                  const [year, month, day] = datePart.split('-').map(Number);
                  const [hour, minute] = timePart.split(':').map(Number);
                  
                  // KST 시간을 UTC timestamp로 직접 변환
                  // KST = UTC + 9시간이므로, UTC = KST - 9시간
                  let utcHour = hour - 9;
                  let utcDay = day;
                  let utcMonth = month;
                  let utcYear = year;
                  
                  // 시간이 음수가 되면 이전 날로 조정
                  if (utcHour < 0) {
                    utcHour += 24;
                    utcDay -= 1;
                    // 날짜가 0이 되면 이전 달로 조정
                    if (utcDay <= 0) {
                      utcMonth -= 1;
                      if (utcMonth <= 0) {
                        utcMonth = 12;
                        utcYear -= 1;
                      }
                      // 해당 월의 마지막 날 계산
                      const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                      utcDay = lastDay;
                    }
                  }
                  
                  const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                  const timestamp = Math.floor(utcTimestamp / 1000);
                  

                  setCreateForm({ ...createForm, startDate: timestamp });
                }}
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date (KST)"
                type="datetime-local"
                value={(() => {
                  const date = new Date(createForm.endDate * 1000);
                  const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
                  // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                  const year = kstDate.getUTCFullYear();
                  const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(kstDate.getUTCDate()).padStart(2, '0');
                  const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                  const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                  const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                  

                  
                  return result;
                })()}
                onChange={(e) => {
                  // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                  const inputValue = e.target.value; // "2025-08-25T21:00"
                  
                  // 입력값을 직접 파싱하여 KST 시간 생성
                  const [datePart, timePart] = inputValue.split('T');
                  const [year, month, day] = datePart.split('-').map(Number);
                  const [hour, minute] = timePart.split(':').map(Number);
                  
                  // KST 시간을 UTC timestamp로 직접 변환
                  // KST = UTC + 9시간이므로, UTC = KST - 9시간
                  let utcHour = hour - 9;
                  let utcDay = day;
                  let utcMonth = month;
                  let utcYear = year;
                  
                  // 시간이 음수가 되면 이전 날로 조정
                  if (utcHour < 0) {
                    utcHour += 24;
                    utcDay -= 1;
                    // 날짜가 0이 되면 이전 달로 조정
                    if (utcDay <= 0) {
                      utcMonth -= 1;
                      if (utcMonth <= 0) {
                        utcMonth = 12;
                        utcYear -= 1;
                      }
                      // 해당 월의 마지막 날 계산
                      const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                      utcDay = lastDay;
                    }
                  }
                  
                  const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                  const timestamp = Math.floor(utcTimestamp / 1000);
                  

                  setCreateForm({ ...createForm, endDate: timestamp });
                }}
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Locale</InputLabel>
                <Select
                  value={createForm.locale}
                  label="Locale"
                  onChange={(e) => setCreateForm({ ...createForm, locale: e.target.value })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="en">English (en)</MenuItem>
                  <MenuItem value="ko">Korean (ko)</MenuItem>
                  <MenuItem value="ja">Japanese (ja)</MenuItem>
                  <MenuItem value="zh">Chinese (zh)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createForm.isActive}
                    onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTextAd} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Text Ad</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  Symbol Image
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => editSymbolImageRef.current?.click()}
                    sx={{ minWidth: 120 }}
                  >
                    Upload Symbol Image
                  </Button>
                  {editSymbolImageFile && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="success.main">
                        ✓ {editSymbolImageFile.name}
                      </Typography>
                      <Box
                        component="img"
                        src={URL.createObjectURL(editSymbolImageFile)}
                        alt="Symbol Image Preview"
                        sx={{
                          width: '100%',
                          maxWidth: 120,
                          height: 'auto',
                          maxHeight: 120,
                          objectFit: 'contain',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                            borderColor: '#1976d2'
                          }
                        }}
                        onClick={() => window.open(URL.createObjectURL(editSymbolImageFile), '_blank')}
                        title="Click to view full image"
                      />
                    </Box>
                  )}
                </Box>
                {!editSymbolImageFile && editingTextAd?.symbolUrl && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Current Image:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Avatar
                        src={editingTextAd.symbolUrl}
                        alt="Symbol"
                        sx={{ width: 120, height: 120, alignSelf: 'flex-start' }}
                      />
                    </Box>
                  </Box>
                )}
                <input
                  id="edit-symbol-image-upload"
                  type="file"
                  ref={editSymbolImageRef}
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      // 파일 크기 체크 (5MB)
                      if (file.size > 5 * 1024 * 1024) {
                        onError('파일 크기는 5MB 이하여야 합니다.');
                        return;
                      }
                      // 파일 타입 체크
                      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
                      if (!allowedTypes.includes(file.type)) {
                        onError('JPG, JPEG, PNG, GIF 파일만 업로드 가능합니다.');
                        return;
                      }
                      setEditSymbolImageFile(file);
                    }
                  }}
                  style={{ display: 'none' }}
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Headline"
                value={editForm.headline}
                onChange={(e) => setEditForm({ ...editForm, headline: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content"
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                margin="normal"
                required
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CTA Text"
                value={editForm.ctaText}
                onChange={(e) => setEditForm({ ...editForm, ctaText: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CTA Link"
                value={editForm.ctaLinkUrl}
                onChange={(e) => setEditForm({ ...editForm, ctaLinkUrl: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date (KST)"
                type="datetime-local"
                value={(() => {
                  const date = new Date(editForm.startDate * 1000);
                  const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
                  // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                  const year = kstDate.getUTCFullYear();
                  const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(kstDate.getUTCDate()).padStart(2, '0');
                  const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                  const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                  const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                  

                  
                  return result;
                })()}
                onChange={(e) => {
                  // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                  const inputValue = e.target.value; // "2025-08-25T21:00"
                  
                  // 입력값을 직접 파싱하여 KST 시간 생성
                  const [datePart, timePart] = inputValue.split('T');
                  const [year, month, day] = datePart.split('-').map(Number);
                  const [hour, minute] = timePart.split(':').map(Number);
                  
                  // KST 시간을 UTC timestamp로 직접 변환
                  // KST = UTC + 9시간이므로, UTC = KST - 9시간
                  let utcHour = hour - 9;
                  let utcDay = day;
                  let utcMonth = month;
                  let utcYear = year;
                  
                  // 시간이 음수가 되면 이전 날로 조정
                  if (utcHour < 0) {
                    utcHour += 24;
                    utcDay -= 1;
                    // 날짜가 0이 되면 이전 달로 조정
                    if (utcDay <= 0) {
                      utcMonth -= 1;
                      if (utcMonth <= 0) {
                        utcMonth = 12;
                        utcYear -= 1;
                      }
                      // 해당 월의 마지막 날 계산
                      const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                      utcDay = lastDay;
                    }
                  }
                  
                  const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                  const timestamp = Math.floor(utcTimestamp / 1000);
                  

                  setEditForm({ ...editForm, startDate: timestamp });
                }}
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date (KST)"
                type="datetime-local"
                value={(() => {
                  const date = new Date(editForm.endDate * 1000);
                  const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
                  // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                  const year = kstDate.getUTCFullYear();
                  const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                  const day = String(kstDate.getUTCDate()).padStart(2, '0');
                  const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                  const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                  const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                  

                  
                  return result;
                })()}
                onChange={(e) => {
                  // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                  const inputValue = e.target.value; // "2025-08-25T21:00"
                  
                  // 입력값을 직접 파싱하여 KST 시간 생성
                  const [datePart, timePart] = inputValue.split('T');
                  const [year, month, day] = datePart.split('-').map(Number);
                  const [hour, minute] = timePart.split(':').map(Number);
                  
                  // KST 시간을 UTC timestamp로 직접 변환
                  // KST = UTC + 9시간이므로, UTC = KST - 9시간
                  let utcHour = hour - 9;
                  let utcDay = day;
                  let utcMonth = month;
                  let utcYear = year;
                  
                  // 시간이 음수가 되면 이전 날로 조정
                  if (utcHour < 0) {
                    utcHour += 24;
                    utcDay -= 1;
                    // 날짜가 0이 되면 이전 달로 조정
                    if (utcDay <= 0) {
                      utcMonth -= 1;
                      if (utcMonth <= 0) {
                        utcMonth = 12;
                        utcYear -= 1;
                      }
                      // 해당 월의 마지막 날 계산
                      const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                      utcDay = lastDay;
                    }
                  }
                  
                  const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                  const timestamp = Math.floor(utcTimestamp / 1000);
                  

                  setEditForm({ ...editForm, endDate: timestamp });
                }}
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Locale</InputLabel>
                <Select
                  value={editForm.locale}
                  label="Locale"
                  onChange={(e) => setEditForm({ ...editForm, locale: e.target.value })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="en">English (en)</MenuItem>
                  <MenuItem value="ko">Korean (ko)</MenuItem>
                  <MenuItem value="ja">Japanese (ja)</MenuItem>
                  <MenuItem value="zh">Chinese (zh)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateTextAd} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TextAdTab;
